import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import PageWrapper from '../layout/PageWrapper/PageWrapper';
import Page from '../layout/Page/Page';
import PageLayoutHeader from '../pages/common/Headers/PageLayoutHeader';
import Alert, { AlertHeading } from '../components/bootstrap/Alert';
import Card, { CardBody, CardHeader, CardLabel, CardActions } from '../components/bootstrap/Card';
import Button from '../components/bootstrap/Button';
import DashboardModule from '../modules/DashboardModule';
import { getRequester, getaActiveRoles } from '../helpers/helpers';
import showNotification from '../components/extras/showNotification';
import useDarkMode from '../hooks/useDarkMode';

const IframeCard = ({ src }) => {
	const containerRef = useRef(null);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const { darkModeStatus } = useDarkMode();

	useEffect(() => {
		const handleChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};
		document.addEventListener('fullscreenchange', handleChange);
		return () => document.removeEventListener('fullscreenchange', handleChange);
	}, []);

	const toggleFullscreen = () => {
		if (!document.fullscreenElement) {
			containerRef.current?.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardLabel icon='Dashboard' iconColor='primary' />
				<CardActions>
					<Button
						isOutline
						icon={isFullscreen ? 'FullscreenExit' : 'Fullscreen'}
						color='primary'
						isLight={darkModeStatus}
						type='button'
						onClick={toggleFullscreen}
					/>
				</CardActions>
			</CardHeader>
			<CardBody>
				<div
					ref={containerRef}
					style={{
						width: '100%',
						height: isFullscreen ? '100vh' : 'auto',
						background: darkModeStatus ? '#1a1a1a' : '#fff',
					}}>
					<iframe
						src={src}
						title={src}
						style={{
							border: 'none',
							display: 'block',
							width: '100%',
							height: isFullscreen ? '100vh' : '600px',
						}}
					/>
				</div>
			</CardBody>
		</Card>
	);
};

IframeCard.propTypes = {
	src: PropTypes.string.isRequired,
};

const Dashboard = () => {
	const [dataReport, setDataReport] = useState([]);
	const { username, person_name } = getRequester();
	const { default_segment_code, default_segment_name, default_role_name } = getaActiveRoles();

	const isRetailSegment = (default_segment_name || '').toLowerCase() === 'retail';

	useEffect(() => {
		if (isRetailSegment) {
			fetchDashboardRetail();
		} else {
			fetchDashboardLegacy();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchDashboardRetail = async () => {
		const query = `username=${username}&segment_code=${default_segment_code}&role_name=${encodeURIComponent(
			default_role_name || '',
		)}`;
		return DashboardModule.getUserLinks(query)
			.then((response) => {
				setDataReport(response.links);
			})
			.catch((err) => {
				showNotification('Information', err, 'danger');
			});
	};

	const fetchDashboardLegacy = async () => {
		const query = `username=${username}&segment_code=${default_segment_code}`;
		return DashboardModule.read(query)
			.then((response) => {
				setDataReport(response.dashboard || []);
			})
			.catch((err) => {
				showNotification('Information', err, 'danger');
			});
	};

	return (
		<PageWrapper title='Dashboard'>
			<PageLayoutHeader />
			<Page container='fluid'>
				<div className='row'>
					<div className='col-12'>
						<Alert
							icon='Verified'
							isLight
							color='success'
							rounded={2}
							borderWidth={0}
							className='shadow-3d-primary'>
							<AlertHeading tag='h2' className='h4'>
								Welcome {person_name || username}
							</AlertHeading>
						</Alert>
					</div>
				</div>
				{dataReport && dataReport.length > 0 && (
					<div className='row'>
						<Card className='col-md-12 shadow-3d-primary w-100'>
							<CardBody>
								<div className='row g-3'>
									{dataReport &&
										dataReport.length > 0 &&
										dataReport.map((x) => (
											<div key={x} className='col-12'>
												<IframeCard src={x} />
											</div>
										))}
								</div>
							</CardBody>
						</Card>
					</div>
				)}
			</Page>
		</PageWrapper>
	);
};

export default Dashboard;
